import { FC, useEffect, useMemo } from 'react';
import {
	Box,
	Button,
	Autocomplete,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
	Grid,
	Divider,
	AlertTitle,
	Alert,
	Link,
	CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router';
import { TRegisterEmDialogProps } from './types';
import { validationSchema } from './validationSchema';
import {
	getCreateEmerchantLoad,
	getCreateEmerchantWarning,
	getCreateEmerchantSuccess,
	getJotformItem,
} from '../../../../redux/reducers/jotform/selectors';
import {
	createEmerchantRequest,
	createEmerchantReset,
	getJotformRequest,
} from '../../../../redux/reducers/jotform/reducer';
import {
	getEnumsSitePlatform,
	getEnumsLegalStatusEu,
	getEnumsPaymentMode,
	getEnumsInvoiceFeeTypeId,
	getEnumsReportingFeeActual,
} from '../../../../redux/reducers/emManagement/selectors';
import { getEmEnumsRequest } from '../../../../redux/reducers/emManagement/reducer';
import { EEmEnums } from '../../../../redux/reducers/emManagement/constants';
import { getCountryListData } from '../../../../redux/reducers/country/selectors';
import { getCountryListRequest } from '../../../../redux/reducers/country/reducer';
import { downloadAttachmentByAttachmentIdRequest } from '../../../../redux/reducers/attachments/reducer';
import { TEmAttachmentRow } from '../../../../redux/reducers/attachments/types';
import { TEmEnumItem } from '../../../../redux/reducers/emManagement/types';

export const RegisterEmDialog: FC<TRegisterEmDialogProps> = ({ open, setClose, jotformId }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const jotform = useSelector(getJotformItem);

	const enumsSitePlatform = useSelector(getEnumsSitePlatform);
	const enumsLegalStatusEu = useSelector(getEnumsLegalStatusEu);
	const enumsPaymentMode = useSelector(getEnumsPaymentMode);
	const enumsInvoiceFeeTypeId = useSelector(getEnumsInvoiceFeeTypeId);
	const enumsReportingFeeActual = useSelector(getEnumsReportingFeeActual);
	const createEmerchantIsLoading = useSelector(getCreateEmerchantLoad);
	const createEmerchantWarningText = useSelector(getCreateEmerchantWarning);
	const createEmerchantSuccess = useSelector(getCreateEmerchantSuccess);
	const countries = useSelector(getCountryListData);

	useEffect(() => {
		if (open && jotformId) {
			dispatch(getJotformRequest({ id: jotformId }));
		}
	}, [dispatch, open, jotformId]);

	useEffect(() => {
		if (open) {
			if (!enumsSitePlatform) dispatch(getEmEnumsRequest({ enumName: EEmEnums.SITE_PLATFORM }));
			if (!enumsLegalStatusEu) dispatch(getEmEnumsRequest({ enumName: EEmEnums.LEGAL_STATUS_EU }));
			if (!enumsPaymentMode) dispatch(getEmEnumsRequest({ enumName: EEmEnums.PAYMENT_MODE }));
			if (!enumsInvoiceFeeTypeId)
				dispatch(getEmEnumsRequest({ enumName: EEmEnums.INVOICE_FEE_TYPE_ID }));
			if (!enumsReportingFeeActual)
				dispatch(getEmEnumsRequest({ enumName: EEmEnums.EM_REPORTING_FEE_ACTUAL }));
			if (!countries) dispatch(getCountryListRequest());
		}
	}, [
		dispatch,
		open,
		enumsSitePlatform,
		enumsLegalStatusEu,
		enumsPaymentMode,
		enumsInvoiceFeeTypeId,
		enumsReportingFeeActual,
		countries,
	]);

	const formik = useFormik({
		initialValues: {
			fullName: '',
			tinNumber: '',
			contactName: '',
			nominatedEmail: '',
			phone: '',
			registeredCountryId: '' as number | '',
			registeredPostalCode: '',
			regAddressCity: '',
			regStreetNo: '',
			officialSiteUrl: '',
			ecommercePlatform: '',
			legalStatus: '',
			paymentHandlingMode: '',
			invoiceFeeTypeId: '' as number | '',
			subscriptionPlanId: '' as number | '',
		},
		validationSchema,
		onSubmit: (values) => {
			if (jotformId) {
				dispatch(
					createEmerchantRequest({
						jotformId,
						data: {
							...values,
							registeredCountryId: Number(values.registeredCountryId),
							invoiceFeeTypeId: Number(values.invoiceFeeTypeId),
							subscriptionPlanId: Number(values.subscriptionPlanId),
							addAn: jotform?.addAn || '',
							configurationMethod28: jotform?.configurationMethod28 || '',
							onboardingSchedule: jotform?.onboardingSchedule || '',
							registeredCountry:
								countries?.find((c) => c.id === values.registeredCountryId)?.name || '',
							isEu: jotform?.isEu || false,
							services: jotform?.services || [],
							iossPlan: jotform?.iossPlan || '',
							ukPlan: jotform?.ukPlan || '',
							nonUnionPlan: jotform?.nonUnionPlan || '',
							ossModel: jotform?.ossModel || '',
							ossPlan: jotform?.ossPlan || '',
							ossEstonianPlan: jotform?.ossEstonianPlan || '',
							prioritySupport: jotform?.prioritySupport || '',
							iossNumber: jotform?.iossNumber || '',
							ukVatNumber: jotform?.ukVatNumber || '',
							isForced: !!createEmerchantWarningText,
						},
					}),
				);
			}
		},
	});

	const selectedSubscriptionPlan = useMemo<TEmEnumItem | null>(() => {
		if (!enumsReportingFeeActual) return null;
		const currentValue = formik.values.subscriptionPlanId;
		if (currentValue === '') return null;
		return (
			enumsReportingFeeActual.find((item) => String(item.value) === String(currentValue)) ?? null
		);
	}, [enumsReportingFeeActual, formik.values.subscriptionPlanId]);

	useEffect(() => {
		if (
			open &&
			createEmerchantWarningText === null &&
			!createEmerchantIsLoading &&
			createEmerchantSuccess
		) {
			dispatch(createEmerchantReset());
			setClose();
			if (createEmerchantSuccess?.id) {
				navigate(`/em-management/${String(createEmerchantSuccess.id)}`);
			}
		}
	}, [
		createEmerchantIsLoading,
		createEmerchantSuccess,
		createEmerchantWarningText,
		setClose,
		open,
		dispatch,
		navigate,
	]);

	const { setValues } = formik;

	useEffect(() => {
		if (jotform) {
			setValues({
				fullName: jotform.fullName || '',
				tinNumber: jotform.tinNumber || '',
				contactName: jotform.contactName || '',
				nominatedEmail: jotform.nominatedEmail || '',
				phone: jotform.phone || '',
				registeredCountryId: jotform.registeredCountryId || '',
				registeredPostalCode: jotform.registeredPostalCode || '',
				regAddressCity: jotform.regAddressCity || '',
				regStreetNo: jotform.regStreetNo || '',
				officialSiteUrl: jotform.officialSiteUrl || '',
				ecommercePlatform: jotform.ecommercePlatform || '',
				legalStatus: jotform.legalStatus || '',
				paymentHandlingMode: jotform.paymentHandlingMode || '',
				invoiceFeeTypeId: jotform.invoiceFeeTypeId || '',
				subscriptionPlanId: '', // User needs to select this
			}).catch(() => {});
		}
	}, [jotform, setValues]);

	const servicesInfo = useMemo(() => {
		if (!jotform) return [];
		const items = [
			{ label: 'IOSS plan', value: jotform.iossPlan },
			{ label: 'UK plan', value: jotform.ukPlan },
			{ label: 'NON Union plan', value: jotform.nonUnionPlan },
			{ label: 'OSS Estonian plan', value: jotform.ossEstonianPlan },
			{ label: 'OSS plan', value: jotform.ossPlan },
			{ label: 'High priority support', value: jotform.prioritySupport },
			{ label: 'Previous IOSS number', value: jotform.iossNumber },
			{ label: 'Requested services', value: jotform.services?.join(', ') },
			{ label: 'Selected configuration method', value: jotform.configurationMethod28 },
			{ label: 'Selected onboarding time', value: jotform.onboardingSchedule },
			{ label: 'Alternative Email', value: jotform.addAn },
		];
		return items.filter((item) => item.value);
	}, [jotform]);

	const handleCancel = () => {
		dispatch(createEmerchantReset());
		setClose();
	};

	const handleDownload = (doc: TEmAttachmentRow) => {
		dispatch(
			downloadAttachmentByAttachmentIdRequest({
				attachmentId: doc?.id,
				fileName: doc?.fileName,
			}),
		);
	};

	return (
		<Dialog
			open={open}
			maxWidth="xl"
			fullWidth
			PaperProps={{
				sx: { borderRadius: '12px' },
			}}
		>
			<DialogTitle color="primary">Register EM</DialogTitle>
			<form
				onSubmit={formik.handleSubmit}
				style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
			>
				<DialogContent
					dividers
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}
				>
					{createEmerchantIsLoading && (
						<>
							<Box
								component="div"
								sx={{
									backgroundColor: 'rgba(255, 255, 255, 0.7)',
									position: 'absolute',
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									zIndex: 1000,
									padding: 0,
									margin: 0,
								}}
							/>

							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									height: '100%',
									width: '100%',
									position: 'absolute',
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									zIndex: 1000,
									padding: 0,
									margin: 0,
								}}
							>
								<CircularProgress size={40} />
							</Box>
						</>
					)}
					{createEmerchantWarningText && (
						<Box sx={{ mb: 2 }}>
							<Alert severity="warning" variant="filled">
								<AlertTitle>Warning</AlertTitle>
								{createEmerchantWarningText.message}
								{createEmerchantWarningText?.data?.em_id && (
									<p>
										<Link
											href={`/em-management/${String(createEmerchantWarningText.data.em_id)}`}
											target="_blank"
											color="primary"
											underline="hover"
										>
											{/* Go to the EM page:{' '} */}
											<strong>
												cc.easproject.com/em-management/
												{String(createEmerchantWarningText.data.em_id)}
											</strong>
										</Link>
									</p>
								)}
								<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
									<Typography variant="body2">
										You can still create this account, even with an address Email already exists if
										you click <strong>&quot;Force Create Account&quot;</strong>.
									</Typography>
									{/* <Button
										type="subm
										variant="contained"
										color="warning"
										disabled={createEmerchantIsLoading}
									>
										Force Create Account
									</Button> */}
								</Box>
							</Alert>
						</Box>
					)}
					<Grid container spacing={3}>
						{/* Left Column: Read-only Info */}
						<Grid item xs={12} md={5}>
							<Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 2, height: '100%' }}>
								<Typography variant="h6" color="primary" gutterBottom>
									Information
								</Typography>

								<Box sx={{ mb: 2 }}>
									<Typography variant="subtitle2">Business Registration Documents:</Typography>
									{jotform?.businessDoc?.map((doc) => (
										<Box key={doc?.id} sx={{ maxWidth: '100%' }}>
											<Typography
												variant="body2"
												sx={{
													cursor: 'pointer',
													textDecoration: 'underline',
													color: 'primary.main',
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													maxWidth: '100%',
												}}
												onClick={() => handleDownload(doc)}
											>
												{doc?.fileName}
											</Typography>
										</Box>
									)) || 'None'}
								</Box>
								<Divider sx={{ my: 1 }} />

								<Box sx={{ mb: 2 }}>
									<Typography variant="subtitle2">Copy of an Official ID:</Typography>
									{jotform?.idDoc?.map((doc) => (
										<Box key={doc?.id} sx={{ maxWidth: '100%' }}>
											<Typography
												variant="body2"
												sx={{
													cursor: 'pointer',
													textDecoration: 'underline',
													color: 'primary.main',
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													maxWidth: '100%',
												}}
												onClick={() => handleDownload(doc)}
											>
												{doc?.fileName}
											</Typography>
										</Box>
									)) || 'None'}
								</Box>
								<Divider sx={{ my: 1 }} />

								<Typography variant="subtitle2" gutterBottom>
									Selected services and Additional information:
								</Typography>
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
									{servicesInfo.map((item) => (
										<Typography key={item.label} variant="body2">
											<strong>{item.label}:</strong> {item.value}
										</Typography>
									))}
								</Box>
							</Box>
						</Grid>

						{/* Right Column: Editable Form */}
						<Grid item xs={12} md={7}>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										label="EM Full Name"
										name="fullName"
										value={formik.values.fullName}
										onChange={formik.handleChange}
										error={formik.touched.fullName && Boolean(formik.errors.fullName)}
										helperText={formik.touched.fullName && formik.errors.fullName}
										size="small"
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										label="TIN"
										name="tinNumber"
										value={formik.values.tinNumber}
										onChange={formik.handleChange}
										error={formik.touched.tinNumber && Boolean(formik.errors.tinNumber)}
										helperText={formik.touched.tinNumber && formik.errors.tinNumber}
										size="small"
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										label="Contact Person"
										name="contactName"
										value={formik.values.contactName}
										onChange={formik.handleChange}
										error={formik.touched.contactName && Boolean(formik.errors.contactName)}
										helperText={formik.touched.contactName && formik.errors.contactName}
										size="small"
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										label="Contact Email"
										name="nominatedEmail"
										value={formik.values.nominatedEmail}
										onChange={formik.handleChange}
										error={formik.touched.nominatedEmail && Boolean(formik.errors.nominatedEmail)}
										helperText={formik.touched.nominatedEmail && formik.errors.nominatedEmail}
										size="small"
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										label="Contact Phone"
										name="phone"
										value={formik.values.phone}
										onChange={formik.handleChange}
										error={formik.touched.phone && Boolean(formik.errors.phone)}
										helperText={formik.touched.phone && formik.errors.phone}
										size="small"
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FormControl
										fullWidth
										size="small"
										error={
											formik.touched.registeredCountryId &&
											Boolean(formik.errors.registeredCountryId)
										}
									>
										<InputLabel>Address Country</InputLabel>
										<Select
											name="registeredCountryId"
											value={formik.values.registeredCountryId}
											label="Address Country"
											onChange={formik.handleChange}
										>
											{countries?.map((country) => (
												<MenuItem key={country.id} value={country.id}>
													{country.name}
												</MenuItem>
											))}
										</Select>
										<FormHelperText>
											{formik.touched.registeredCountryId && formik.errors.registeredCountryId}
										</FormHelperText>
									</FormControl>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										label="Address ZIP"
										name="registeredPostalCode"
										value={formik.values.registeredPostalCode}
										onChange={formik.handleChange}
										error={
											formik.touched.registeredPostalCode &&
											Boolean(formik.errors.registeredPostalCode)
										}
										helperText={
											formik.touched.registeredPostalCode && formik.errors.registeredPostalCode
										}
										size="small"
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										label="Address City"
										name="regAddressCity"
										value={formik.values.regAddressCity}
										onChange={formik.handleChange}
										error={formik.touched.regAddressCity && Boolean(formik.errors.regAddressCity)}
										helperText={formik.touched.regAddressCity && formik.errors.regAddressCity}
										size="small"
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										label="Address Street"
										name="regStreetNo"
										value={formik.values.regStreetNo}
										onChange={formik.handleChange}
										error={formik.touched.regStreetNo && Boolean(formik.errors.regStreetNo)}
										helperText={formik.touched.regStreetNo && formik.errors.regStreetNo}
										size="small"
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										label="Official Site"
										name="officialSiteUrl"
										value={formik.values.officialSiteUrl}
										onChange={formik.handleChange}
										error={formik.touched.officialSiteUrl && Boolean(formik.errors.officialSiteUrl)}
										helperText={formik.touched.officialSiteUrl && formik.errors.officialSiteUrl}
										size="small"
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FormControl
										fullWidth
										size="small"
										error={
											formik.touched.ecommercePlatform && Boolean(formik.errors.ecommercePlatform)
										}
									>
										<InputLabel>Store Platform</InputLabel>
										<Select
											name="ecommercePlatform"
											value={formik.values.ecommercePlatform}
											label="Store Platform"
											onChange={formik.handleChange}
										>
											{enumsSitePlatform?.map((item) => (
												<MenuItem key={item.value} value={item.value}>
													{item.displayName}
												</MenuItem>
											))}
										</Select>
										<FormHelperText>
											{formik.touched.ecommercePlatform && formik.errors.ecommercePlatform}
										</FormHelperText>
									</FormControl>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FormControl
										fullWidth
										size="small"
										error={formik.touched.legalStatus && Boolean(formik.errors.legalStatus)}
									>
										<InputLabel>Legal Status</InputLabel>
										<Select
											name="legalStatus"
											value={formik.values.legalStatus}
											label="Legal Status"
											onChange={formik.handleChange}
										>
											{enumsLegalStatusEu?.map((item) => (
												<MenuItem key={item.value} value={item.value}>
													{item.displayName}
												</MenuItem>
											))}
										</Select>
										<FormHelperText>
											{formik.touched.legalStatus && formik.errors.legalStatus}
										</FormHelperText>
									</FormControl>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FormControl
										fullWidth
										size="small"
										error={
											formik.touched.paymentHandlingMode &&
											Boolean(formik.errors.paymentHandlingMode)
										}
									>
										<InputLabel>Payment Handling Mode</InputLabel>
										<Select
											name="paymentHandlingMode"
											value={formik.values.paymentHandlingMode}
											label="Payment Handling Mode"
											onChange={formik.handleChange}
										>
											{enumsPaymentMode?.map((item) => (
												<MenuItem key={item.value} value={item.value}>
													{item.displayName}
												</MenuItem>
											))}
										</Select>
										<FormHelperText>
											{formik.touched.paymentHandlingMode && formik.errors.paymentHandlingMode}
										</FormHelperText>
									</FormControl>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FormControl
										fullWidth
										size="small"
										error={
											formik.touched.invoiceFeeTypeId && Boolean(formik.errors.invoiceFeeTypeId)
										}
									>
										<InputLabel>Invoicing Fee</InputLabel>
										<Select
											name="invoiceFeeTypeId"
											value={formik.values.invoiceFeeTypeId}
											label="Invoicing Fee"
											onChange={formik.handleChange}
										>
											{enumsInvoiceFeeTypeId?.map((item) => (
												<MenuItem key={item.value} value={item.value}>
													{item.displayName}
												</MenuItem>
											))}
										</Select>
										<FormHelperText>
											{formik.touched.invoiceFeeTypeId && formik.errors.invoiceFeeTypeId}
										</FormHelperText>
									</FormControl>
								</Grid>
								<Grid item xs={12} sm={6}>
									<Autocomplete
										size="small"
										fullWidth
										options={enumsReportingFeeActual ?? []}
										value={selectedSubscriptionPlan}
										onChange={(event, newValue) => {
											formik
												.setFieldValue('subscriptionPlanId', newValue?.value ?? '')
												.catch(() => {});
										}}
										isOptionEqualToValue={(option, value) => option.value === value.value}
										getOptionLabel={(option) => option.displayName}
										renderInput={(params) => (
											<TextField
												{...params}
												name="subscriptionPlanId"
												label="Monthly Subscription"
												onBlur={() => {
													formik.setFieldTouched('subscriptionPlanId', true).catch(() => {});
												}}
												error={
													formik.touched.subscriptionPlanId &&
													Boolean(formik.errors.subscriptionPlanId)
												}
												helperText={
													formik.touched.subscriptionPlanId && formik.errors.subscriptionPlanId
												}
											/>
										)}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions sx={{ p: 2 }}>
					<Button onClick={handleCancel} color="inherit">
						Cancel
					</Button>
					{createEmerchantWarningText ? (
						<Button
							type="submit"
							variant="contained"
							color="warning"
							disabled={createEmerchantIsLoading}
						>
							Force Create Account
						</Button>
					) : (
						<Button
							type="submit"
							variant="contained"
							color="primary"
							disabled={createEmerchantIsLoading}
						>
							Submit
						</Button>
					)}
				</DialogActions>
			</form>
		</Dialog>
	);
};
